import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../styles/SubscriptionContainer.module.scss';
import { usePurchaseSubscriptionContext } from '../../../PurchaseSubscription/context/PurchaseSubscriptionContext';
import AppPaths from '../../../../AppPaths';
import { useHistory, useLocation } from 'react-router-dom';
import SubscriptionAppBar from '../../components/SubscriptionAppBar';
import Button from '../../../../components/Button';
import { inAppCancellation as events } from '../../../../lib/analytics/events';
import { Carousel, CarouselController } from '../../../../components/Carousel';
import { gqlTypes } from '../../../../types';
import { useZendeskCancellationTicket } from '../hooks/useZendesk';
import classNames from 'classnames';
import lunaImg from '../../../../assets/images/in_app_cancellation/luna.png';
import lunaOwnerImg from '../../../../assets/images/in_app_cancellation/luna_owner.png';
import lunaStepCountImg from '../../../../assets/images/in_app_cancellation/luna_step_count.png';
import lunaXrayImg from '../../../../assets/images/in_app_cancellation/luna_xray.png';
import CancelConfirmationModal from '../../components/CancelConfirmationModal';
import { BaseInAppCancellationState, SubscriptionCategory } from '../util/types';

export default function DogDoesNotRunAway() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<BaseInAppCancellationState>();
  const { subscriptionCategory } = location.state;
  const { device } = usePurchaseSubscriptionContext();
  const [selectedPageIdx, setSelectedPageIdx] = useState(0);
  const [showNextButton, setShowNextButton] = useState(true);
  const carouselControllerRef = useRef<CarouselController>();
  const { mutation, loading } = useZendeskCancellationTicket({
    analyticsEvent: events.dogDoesNotRunAwayCancel,
    selectedCancelReason: gqlTypes.zendeskCancellationReason.DOG_DOES_NOT_RUN_AWAY,
    device,
    subscriptionCategory,
  });

  const showUpgradeVersions: string[] = ['S1', 'S2'];
  const deviceModel = device.currentCollarModel ?? '';

  if (!device.subscription?.id) {
    throw new Error('No recurly subscription found.');
  }

  if (loading) {
    return loading;
  }

  const offerFreeMonth = !!device.shouldOfferFreeMonthDuringCancellation;
  const offerS3Upgrade = showUpgradeVersions.includes(deviceModel);

  const handleKeep = () => {
    events.dogDoesNotRunAwayKeepMembership({ petId: device.pet?.id, subscriptionCategory });
    history.push({
      pathname: AppPaths.Subscription.KeepMembershipConfirmation(device.moduleId),
      state: { selectedCancelReason: gqlTypes.zendeskCancellationReason.DOG_DOES_NOT_RUN_AWAY },
      search: window.location.search,
    });
  };

  const handleRetentionOffers = () => {
    events.dogDoesNotRunAwayContinue({ petId: device.pet?.id, subscriptionCategory });
    if (subscriptionCategory === SubscriptionCategory.MONTHLY) {
      // Take S3 monthly users to special offers screen.
      history.push({
        pathname: AppPaths.Subscription.SpecialOffers(device.moduleId),
        state: { selectedCancelReason: gqlTypes.zendeskCancellationReason.DOG_DOES_NOT_RUN_AWAY, subscriptionCategory },
        search: window.location.search,
      });
    } else if (offerS3Upgrade) {
      // Take S1 and S2 users to upgrade flow.
      history.push({
        pathname: AppPaths.Subscription.Upgrade(device.moduleId),
        state: { selectedCancelReason: gqlTypes.zendeskCancellationReason.DOG_DOES_NOT_RUN_AWAY, subscriptionCategory },
        search: window.location.search,
      });
    } else if (offerFreeMonth) {
      history.push({
        pathname: AppPaths.Subscription.FreeMonthOffer(device.moduleId),
        state: { selectedCancelReason: gqlTypes.zendeskCancellationReason.DOG_DOES_NOT_RUN_AWAY, subscriptionCategory },
        search: window.location.search,
      });
    } else {
      throw new Error('Something went wrong.');
    }
  };

  const handleCancel = () => {
    events.dogDoesNotRunAwayContinue({ petId: device.pet?.id, subscriptionCategory });
    mutation({
      variables: {
        input: {
          recurlySubscriptionId: device.subscription!.id,
          cancellationReason: gqlTypes.zendeskCancellationReason.DOG_DOES_NOT_RUN_AWAY,
          additionalText: undefined,
        },
      },
    });
  };

  return (
    <>
      <div className={styles.fullWidthWrapper}>
        <SubscriptionAppBar
          backButtonAnalyticsEvent={() =>
            events.dogDoesNotRunAwayBack({ petId: device.pet?.id, subscriptionCategory }, selectedPageIdx + 1)
          }
          closeButtonAnalyticsEvent={() =>
            events.dogDoesNotRunAwayClose({ petId: device.pet?.id, subscriptionCategory }, selectedPageIdx + 1)
          }
        />
        <div className={styles.subscriptionContainer}>
          <div className={styles.imagesCarouselContainer}>
            <Carousel
              cellAlign="center"
              className={styles.imageCarousel}
              pageDots={true}
              controllerRef={carouselControllerRef}
              prevNextButtons={true}
              onChange={(idx) => {
                idx === 2 ? setShowNextButton(false) : setShowNextButton(true);
                const analyticsPageIdx = selectedPageIdx + 1;
                if (idx > selectedPageIdx) {
                  events.dogDoesNotRunAwayNext({ petId: device.pet?.id, subscriptionCategory }, analyticsPageIdx);
                } else if (idx < selectedPageIdx) {
                  events.dogDoesNotRunAwayBack({ petId: device.pet?.id, subscriptionCategory }, analyticsPageIdx);
                }
                setSelectedPageIdx(idx);
              }}
            >
              <div className={styles.imageCarouselImage}>
                <div className={styles.largeHeader}>
                  <h1>{t('fi_reveals_more_than_just_location')}</h1>
                </div>

                <img
                  className={styles.stepCount}
                  src={lunaStepCountImg}
                  alt={t('a_bar_graph_showing_declining_step_count_day_over_day')}
                />
                <p>
                  {t(
                    'data_can_often_say_what_your_dog_cant_when_super_active_frenchie_luna_had_taken_a_20000_step_plunge',
                  )}
                </p>
              </div>
              <div className={classNames(styles.imageCarouselImage, styles.xray)}>
                <div className={styles.largeHeader}>
                  <h1>{t('decoding_lunas_lack_of_activity')}</h1>
                </div>
                <img src={lunaXrayImg} alt={t('dog_xray_showing_a_swallowed_rock')} />
                <p>
                  {t(
                    'turns_out_she_had_swallowed_a_rock_and_developed_an_abdominal_obstruction_an_xray_confirmed_what_the_data_was_suggesting',
                  )}
                </p>
              </div>
              <div className={styles.imageCarouselImage}>
                <div className={styles.largeHeader}>
                  <h1>{t('better_health_outcomes_with_fi')}</h1>
                </div>
                <img src={lunaImg} alt={t('a_dog_walking_towards_camera_wearing_a_fi_collar')} />
                <div className={styles.testimonial}>
                  <img src={lunaOwnerImg} alt={t('owner_profile_pic')} className={styles.lunaOwner} />
                  <p>
                    {t(
                      'because_we_got_her_help_early_none_of_lunas_intestines_became_necrotic_and_no_dangerous_resection_was_required',
                    )}
                  </p>
                </div>
              </div>
            </Carousel>
          </div>
        </div>
        <div className={styles.buttonContainer}>
          {showNextButton ? (
            <>
              <Button
                className={styles.button}
                onClick={() => {
                  const temp: any = document.querySelector('.flickity-button.flickity-prev-next-button.next');
                  temp.click();
                }}
              >
                {t('next')}
              </Button>
              {/* Spacer ensures primary button is aligned with buttons on neighboring pages in in-app cancel flow */}
              <div className={styles.buttonSpacer}></div>
            </>
          ) : (
            <>
              <Button className={styles.button} onClick={handleKeep}>
                {t('keep_membership')}
              </Button>

              {offerS3Upgrade || offerFreeMonth ? (
                <Button className={styles.button} tertiary={true} onClick={handleRetentionOffers}>
                  {t('continue_with_cancellation')}
                </Button>
              ) : (
                <CancelConfirmationModal
                  onContinue={() => handleCancel()}
                  pageName={t('dog_does_not_run_away')}
                  petId={device.pet!.id}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
